import { useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { useReduxEntitiesFetch } from "@ds/hooks/use-redux-entities-fetch";
import { useDeepRootSelector, useRootDispatch, useRootSelector } from "@ds/hooks";

import { EntityTypeName } from "@ds/model/entity-model";

import { MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { ActionsBar } from "@ds/components/actions-bar";
import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { IconType } from "@ds/components/icons";
import { InfoViewToggleRibbonButton } from "@ds/components/info-view/info-view-toggle-ribbon-button";
import { confirmWarning } from "@ds/components/popups/confirmation-dialog";
import { pluralizeDeleteMessage } from "@ds/utils/localization/common";
import { composeDetailsRouteByEntityType } from "@ds/utils/router";

import { selectAggregatedCampaignForSelectedEntities, selectSelectedCampaignIds } from "../../redux/selectors";
import { campaignsActions } from "../../redux/slice";

export const CampaignsTableActionsMenu: FC<{ onCreate: () => void; onDuplicate: () => void }> = ({
  onCreate,
  onDuplicate,
}) => {
  const navigate = useNavigate();

  const aggregatedCampaign = useDeepRootSelector(selectAggregatedCampaignForSelectedEntities);
  const selectedCampaignIds = useRootSelector(selectSelectedCampaignIds);

  const [toggleRefetch] = useReduxEntitiesFetch(
    { entityType: EntityTypeName.CONTENT_CAMPAIGN },
    { tableType: MainTableDataTypeEnum.ContentCampaigns },
  );

  const dispatch = useRootDispatch();
  const onUpdateClickHandler = useCallback(
    () => navigate(composeDetailsRouteByEntityType(aggregatedCampaign)),
    [navigate, aggregatedCampaign],
  );

  const onDeleteClickHandler = useCallback(
    () =>
      confirmWarning({
        message: pluralizeDeleteMessage("campaign", selectedCampaignIds.length),
        accept: () => dispatch(campaignsActions.deleteCampaigns(selectedCampaignIds)),
      }),
    [dispatch, selectedCampaignIds],
  );

  return (
    <ActionsBar>
      <Button ariaLabel="create button" buttonType={ButtonType.Round} icon={IconType.Create} onClick={onCreate} />
      <Button
        ariaLabel="edit button"
        buttonType={ButtonType.Round}
        icon={IconType.Update}
        isDisabled={!selectedCampaignIds.length}
        onClick={onUpdateClickHandler}
      />

      {selectedCampaignIds.length === 1 && (
        <Button
          ariaLabel="duplicate button"
          buttonType={ButtonType.Round}
          icon={IconType.Duplicate}
          onClick={onDuplicate}
        />
      )}

      <Button
        ariaLabel="delete button"
        buttonType={ButtonType.Round}
        actionType={ButtonActionType.Error}
        icon={IconType.Delete}
        isDisabled={!selectedCampaignIds.length}
        onClick={onDeleteClickHandler}
      />

      <Button
        ariaLabel="refresh button"
        buttonType={ButtonType.Round}
        icon={IconType.Refresh}
        onClick={toggleRefetch}
      />

      <InfoViewToggleRibbonButton />
    </ActionsBar>
  );
};
